import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { getStyles, translate, localizeDateTime } from '../../../shared/utility';
import { isDate, modifyDate, durationToMS } from './DateTimeHelpers';

import Icon from '../Icon/Icon';
import Tag from '../Tag/Tag';
import Button from '../Button/Button';

import DatePicker from './DatePicker/DatePicker';
import TimePicker from './TimePicker/TimePicker';

import localStyles from './DateTimePicker.scss';
const styles = getStyles(localStyles);

class DateTimePicker extends Component {

    /* * * * * * * * * * * * * *
     * REACT LIFECYCLE METHODS *
     * * * * * * * * * * * * * */
    constructor(props) {
        super(props);

        this.state = {
            type: props.button ? 'date' : props.type,
            isRange: props.range,
            outputFormat: props.outputFormat,
            immediateCallback: props.immediateCallback !== undefined ? props.immediateCallback : true,

            min: props.min ? modifyDate(props.min) : null,
            max: props.max ? modifyDate(props.max) : null,

            min_range: durationToMS(props.minRange || 'PT1M'),
            max_range: durationToMS(props.maxRange || 'P3Y'),

            earliest: props.earliest ? modifyDate(props.earliest) : modifyDate(new Date(), {hours: 0, minutes: 0}),
            latest: props.latest ? modifyDate(props.latest) : modifyDate(new Date(), {hours: 23, minutes: 59}),

            stepsize: durationToMS(props.stepsize || 'PT15M'),
            roundStepsize: props.roundStepsize !== undefined ? props.roundStepsize : true,

            placeholder_date: props.datePlaceholder || translate('ui.selectDate', props.language),
            placeholder_time: props.timePlaceholder || '--:--',

            focus_date_start: false,
            focus_time_start: false,
            focus_date_end: false,
            focus_time_end: false,
            datepicker_open: false,
            timepicker_start_open: false,
            timepicker_end_open: false,

            val_start: null,
            val_end: null,

            start_date_set: false,
            start_time_set: false,
            end_date_set: false,
            end_time_set: false
        };

        this.refPicker = React.createRef();
    }

    componentDidMount(){
        document.addEventListener('click', this.handleDocumentClick);
        const newState = this.propsToNewState({}, this.props);
        if(Object.keys(newState).length){
            this.setState(newState);
        }
    }

    componentDidUpdate(prevProps, prevState){

        if(this.state.immediateCallback &&
        ((this.state.val_start !== prevState.val_start && this.state.val_start - prevState.val_start !== 0) ||
        (this.state.val_end !== prevState.val_end && this.state.val_end - prevState.val_end !== 0) ||
        (this.state.start_date_set !== prevState.start_date_set) ||
        (this.state.end_date_set !== prevState.end_date_set) ||
        (this.state.start_time_set !== prevState.start_time_set) ||
        (this.state.end_time_set !== prevState.end_time_set))){
            this.output();
        }

        const newState = this.propsToNewState(prevProps, this.props);
        if(Object.keys(newState).length){
            this.setState(newState);
        }
    }

    componentWillUnmount(){
        document.removeEventListener('click', this.handleDocumentClick);
    }

    propsToNewState = (prevProps = {}, currProps = {}) => {
        const newState = {};

        const hasChanged = (prop) => prevProps[prop] !== currProps[prop] && currProps[prop] !== undefined;

        const dateModComponents = this.state.type === 'date' ? {hours:0, minutes: 0} : {};

        // default settings
        if(hasChanged('type')){
            newState.type = currProps.button ? 'date' : currProps.type;
        }
        if(hasChanged('range')){
            newState.isRange = currProps.range;
        }
        if(hasChanged('outputFormat')){
            newState.outputFormat = currProps.outputFormat;
        }

        // datetime propterties
        if(hasChanged('value')){
            newState.val_start = modifyDate(currProps.value, dateModComponents);
            newState.start_date_set = isDate(newState.val_start);
            newState.start_time_set = isDate(newState.val_start);
        }
        if(hasChanged('start')){
            newState.val_start = modifyDate(currProps.start, dateModComponents);
            newState.start_date_set = isDate(newState.val_start);
            newState.start_time_set = isDate(newState.val_start);
        }
        if(hasChanged('end')){
            newState.val_end = modifyDate(currProps.end, dateModComponents);
            newState.end_date_set = isDate(newState.val_start);
            newState.end_time_set = isDate(newState.val_start);
        }
        // date properties
        if(hasChanged('date') && isDate(currProps.date)){
            newState.val_start = modifyDate(this.state.val_start || new Date(), {
                fullYear: currProps.date.getFullYear(),
                month: currProps.date.getMonth(),
                date: currProps.date.getDate()
            });
            newState.start_date_set = true;
        }
        if(hasChanged('startDate') && isDate(currProps.startDate)){
            newState.val_start = modifyDate(this.state.val_start || new Date(), {
                fullYear: currProps.startDate.getFullYear(),
                month: currProps.startDate.getMonth(),
                date: currProps.startDate.getDate()
            });
            newState.start_date_set = true;
        }
        if(hasChanged('endDate') && isDate(currProps.endDate)){
            newState.val_end = modifyDate(this.state.val_end || new Date(), {
                fullYear: currProps.endDate.getFullYear(),
                month: currProps.endDate.getMonth(),
                date: currProps.endDate.getDate()
            });
            newState.end_date_set = true;
        }
        // time properties
        if(hasChanged('time') && isDate(currProps.time)){
            newState.val_start = modifyDate(this.state.val_start || new Date(), {
                hours: currProps.time.getHours(),
                minutes: currProps.time.getMinutes()
            });
            newState.start_time_set = true;
        }
        if(hasChanged('startTime') && isDate(currProps.startTime)){
            newState.val_start = modifyDate(this.state.val_start || new Date(), {
                hours: currProps.startTime.getHours(),
                minutes: currProps.startTime.getMinutes()
            });
            newState.start_time_set = true;
        }
        if(hasChanged('endTime') && isDate(currProps.endTime)){
            newState.val_end = modifyDate(this.state.val_end || new Date(), {
                hours: currProps.endTime.getHours(),
                minutes: currProps.endTime.getMinutes()
            });
            newState.end_time_set = true;
        }

        // ranges
        if(hasChanged('min')){
            newState.min = modifyDate(currProps.min);
        }
        if(hasChanged('max')){
            newState.max = modifyDate(currProps.max);
        }
        if(hasChanged('earliest')){
            newState.earliest = modifyDate(currProps.earliest);
        }
        if(hasChanged('latest')){
            newState.latest = modifyDate(currProps.latest);
        }
        if(hasChanged('minRange')){
            newState.min_range = durationToMS(currProps.minRange);
        }
        if(hasChanged('maxRange')){
            newState.max_range = durationToMS(currProps.maxRange);
        }

        //steps
        if(hasChanged('stepsize')){
            newState.stepsize = durationToMS(currProps.stepsize);
        }
        if(hasChanged('roundStepsize')){
            newState.roundStepsize = currProps.roundStepsize;
        }

        // placeholders
        if(hasChanged('datePlaceholder')){
            newState.placeholder_date = currProps.datePlaceholder;
        }
        if(hasChanged('timePlaceholder')){
            newState.placeholder_time = currProps.timePlaceholder;
        }

        return newState;
    }

    /* * * * * *
     * EVENTS  *
     * * * * * */
    output = () => {
        if(typeof this.props.callback !== 'function'){
            return;
        }

        const startStatesToCheck = [];
        switch(this.state.type){
            case 'date':
                startStatesToCheck.push('start_date_set');
                break;
            case 'time':
                startStatesToCheck.push('start_time_set');
                break;
            case 'datetime':
            default:
                startStatesToCheck.push('start_date_set', 'start_time_set');
        }

        const endStatesToCheck = this.state.isRange ? startStatesToCheck.map(state => state.replace('start', 'end')) : [];

        const output = [
            !isDate(this.state.val_start) || startStatesToCheck.filter(state => !this.state[state]).length ? null : this.state.val_start,
            !isDate(this.state.val_end) || endStatesToCheck.filter(state => !this.state[state]).length || !this.state.isRange ? null : this.state.val_end
        ].map(date => !isDate(date) ? null : this.state.outputFormat === 'iso' ? date.toISOString() : this.state.outputFormat === 'timestamp' ? date.getTime() : new Date(date));

        this.props.callback(output);
    }

    setDate = (date, dateOrTime, startOrEnd) => {
        if(isDate(date)){
            const changedDate = isDate(this.state[`val_${startOrEnd}`]) ? new Date(this.state[`val_${startOrEnd}`]) : new Date(0,0,0,0,0,0,0);
            const dateModComponents = dateOrTime === 'date' ?
                {fullYear: date.getFullYear(), month: date.getMonth(), date: date.getDate()} :
                {hours: date.getUTCHours(), minutes: date.getUTCMinutes()};

            this.setState({
                [`val_${startOrEnd}`]: modifyDate(changedDate, dateModComponents),
                [`${startOrEnd}_${dateOrTime}_set`]: true
            });
        } else {
            this.setState({[`${startOrEnd}_${dateOrTime}_set`]: false});
        }
    };

    /* * * * * * * * * * * *
     * OPEN CLOSE PICKERS  *
     * * * * * * * * * * * */
    openClosePickers = (open = false, dateOrTime = 'date', startOrEnd = 'start') => {
        this.setState({
            datepicker_open: dateOrTime === 'date' && open,
            timepicker_start_open: dateOrTime === 'time' && startOrEnd === 'start' && open,
            timepicker_end_open: dateOrTime === 'time' && startOrEnd === 'end' && open,
            focus_date_start: dateOrTime === 'date' && startOrEnd === 'start' && open,
            focus_date_end: dateOrTime === 'date' && startOrEnd === 'end' && open,
            focus_time_start: dateOrTime === 'time' && startOrEnd === 'start' && open,
            focus_time_end: dateOrTime === 'time' && startOrEnd === 'end' && open
        });
    };
    handleDocumentClick = (event) => {
        if(this.refPicker && this.refPicker.current && !this.refPicker.current.contains(event.target)){
            this.openClosePickers();
        }
    };

    /* * * * * * * * * * *
     * GENERATE DISPLAYS *
     * * * * * * * * * * */
    getPlaceholder = (dateOrTime) => dateOrTime === 'date' ? this.props.button ? this.state.placeholder_date : <Tag>{this.state.placeholder_date}</Tag> : this.state.placeholder_time;

    getDisplayFormatSettings = (dateOrTime) => ({
        year: dateOrTime === 'date' ? 'numeric' : undefined,
        month: dateOrTime === 'date' ? this.state.type === 'date' ? 'long' : '2-digit' : undefined,
        day: dateOrTime === 'date' ? this.state.type === 'date' ? 'numeric' : '2-digit' : undefined,
        weekday: dateOrTime === 'date' && this.state.type === 'date' ? 'long' : undefined,
        hour: dateOrTime === 'time' ? '2-digit' : undefined,
        minute: dateOrTime === 'time' ? '2-digit' : undefined
    });

    getDisplayValue = (dateOrTime, startOrEnd) => {
        if(startOrEnd === 'both'){
            if(this.state[`start_${dateOrTime}_set`] && !this.state[`end_${dateOrTime}_set`]){
                return localizeDateTime(this.state.val_start, this.props.locale, {day: 'numeric', month: 'short'});
            }else if(this.state[`end_${dateOrTime}_set`] && !this.state[`start_${dateOrTime}_set`]){
                return localizeDateTime(this.state.val_end, this.props.locale, {day: 'numeric', month: 'short'});
            }else if(this.state[`end_${dateOrTime}_set`] && this.state[`start_${dateOrTime}_set`]){
                return localizeDateTime(this.state.val_start, this.props.locale, {day: 'numeric', month: 'short'}) + ' - ' + localizeDateTime(this.state.val_end, this.props.locale, {day: 'numeric', month: 'short'});
            }else{
                return this.getPlaceholder(dateOrTime);
            }
        }

        const value = this.state[`val_${startOrEnd}`];
        if(value === null || !this.state[`${startOrEnd}_${dateOrTime}_set`]){
            return this.getPlaceholder(dateOrTime);
        }
        return localizeDateTime(value, this.props.locale, this.getDisplayFormatSettings(dateOrTime));
    };
    generateDateDisplay = (startOrEnd) => (
        <div
            className={styles([
                'picker__display', 'picker__display--date',
                `picker__display--date--${this.state.type}`,
                `picker__display--date--${startOrEnd}`,
                (this.state[`focus_date_${startOrEnd}`] ? 'picker__display--focus' : ''),
                (this.state[`focus_date_${startOrEnd}`] ? 'picker__display--date--focus' : ''),
                (this.state[`focus_date_${startOrEnd}`] ? `picker__display--date--${this.state.type}--focus` : '')
            ])}
            onClick={() => this.openClosePickers(true, 'date', startOrEnd)}
        >
            <Icon icon={'calendar'} modifiers={['inline']} />
            <span className={styles('picker__value')}>
                {this.getDisplayValue('date', startOrEnd)}
            </span>
        </div>
    );
    generateTimeDisplay = (startOrEnd) => (
        <div
            className={styles([
                'picker__display', 'picker__display--time',
                `picker__display--time--${this.state.type}`,
                `picker__display--time--${startOrEnd}`,
                (this.state.type === 'datetime' ? 'align--center' : ''),
                (this.state[`focus_time_${startOrEnd}`] ? 'picker__display--focus' : ''),
                (this.state[`focus_time_${startOrEnd}`] ? 'picker__display--time--focus' : ''),
                (this.state[`focus_time_${startOrEnd}`] ? `picker__display--time--${this.state.type}--focus` : '')
            ])}
            onClick={(event) => {
                if(event.target.classList.contains(styles('picker__display--time')) || event.target.classList.contains(styles('picker__value'))){
                    this.openClosePickers(true, 'time', startOrEnd);
                }
            }}
        >
            {this.state.type === 'time' ? <Icon icon={'clock'} modifiers={['inline']} /> : null}
            <span className={styles('picker__value')}>
                {this.getDisplayValue('time', startOrEnd)}
            </span>
            <TimePicker locale={this.props.locale} language={this.props.language}
                isOpen={this.state[`timepicker_${startOrEnd}_open`]}

                minDate={this.state.min || 'today'}
                maxDate={this.state.max}
                minRange={this.state.min_range}
                maxRange={this.state.max_range}

                earliest={this.state.earliest}
                latest={this.state.latest}

                stepsize={this.state.stepsize}
                roundStepsize={this.state.roundStepsize}

                value={this.state[`val_${startOrEnd}`]}
                set={this.state[`${startOrEnd}_time_set`]}

                close={() => this.openClosePicker()}
                select={(date) => this.setDate(date, 'time', startOrEnd)}
                focusNext={() => {
                    if(this.state.isRange && this.state.focus_time_start){
                        this.openClosePickers(true, this.state.type === 'datetime' ? 'date' : 'time', 'end');
                    }else{
                        this.openClosePickers();
                    }
                }}
            />
        </div>
    );

    /* * * * * *
     * RENDER  *
     * * * * * */
    render(){
        const wrapperClasses = [
            'picker',
            `picker--${this.state.type}`,
            (this.state.isRange ? `picker--${this.state.type}--range` : ''),
            (this.props.button ? 'picker--button' : 'flex'),
            (this.props.button ? '' : 'flex--wrap')
        ];

        const boxClasses = [
            'picker__box',
            `picker__box--${this.state.type}`,
            'flex'
        ];

        const showDateStart = this.state.type !== 'time';
        const showDateEnd = this.state.type !== 'time' && this.state.isRange;
        const showTimeStart = this.state.type !== 'date';
        const showTimeEnd = this.state.type !== 'date' && this.state.isRange;

        return (
            <Fragment>
                <div className={styles(wrapperClasses)} ref={this.refPicker}>
                    {this.props.button ? (
                        <Button
                            modifiers={['white', 'border']}
                            classes={styles('picker__button', this.state.start_date_set || this.state.end_date_set ? 'picker__button--filledout' : '')}
                            events={{click: () => {
                                this.state.date_picker_open ? this.openClosePickers() : this.openClosePickers(true, 'date');
                            }}}
                        >
                            {this.getDisplayValue('date', 'both')}
                        </Button>
                    ) : (
                        <Fragment>
                            <div className={styles(boxClasses)}>
                                {showDateStart ? this.generateDateDisplay('start') : null}
                                {showTimeStart ? this.generateTimeDisplay('start') : null}
                            </div>
                            {showDateEnd || showTimeEnd ? (
                                <div className={styles(boxClasses)}>
                                    {showDateEnd ? this.generateDateDisplay('end') : null}
                                    {showTimeEnd ? this.generateTimeDisplay('end') : null}
                                </div>
                            ) : null}
                        </Fragment>
                    )}
                    {this.state.type === 'date' || this.state.type === 'datetime' ? (
                        <DatePicker locale={this.props.locale} language={this.props.language}
                            isOpen={this.state.datepicker_open}
                            isRange={this.state.isRange}

                            minDate={this.state.min || 'today'}
                            maxDate={this.state.max}
                            minRange={this.state.min_range}
                            maxRange={this.state.max_range}

                            startValue={this.state.start_date_set ? this.state.val_start : null}
                            endValue={this.state.end_date_set ? this.state.val_end : null}
                            focus={this.state.focus_date_end ? 'end' : 'start'}

                            invalidDates={null}

                            close={() => {
                                this.openClosePickers();
                                if(!this.state.immediateCallback){
                                    this.output();
                                }
                            }}
                            select={this.setDate}
                            focusNext={() => {
                                if(this.state.type === 'datetime'){
                                    if(this.state.focus_date_start){
                                        this.openClosePickers(true, 'time', 'start');
                                    }else if(this.state.focus_date_end){
                                        this.openClosePickers(true, 'time', 'end');
                                    }
                                }else if(this.state.isRange){
                                    if(this.state.focus_date_start){
                                        this.openClosePickers(true, 'date', 'end');
                                    }else if(this.state.focus_date_end){
                                        this.openClosePickers(true, 'date', 'start');
                                    }
                                }
                            }}
                        />
                    ) : null}
                </div>
            </Fragment>
        );
    }
}

DateTimePicker.propTypes = {
    type: PropTypes.oneOf(['date', 'time', 'datetime']).isRequired,
    range: PropTypes.bool.isRequired,
    outputFormat: PropTypes.oneOf(['iso', 'timestamp', 'date']).isRequired,
    button: PropTypes.bool,
    immediateCallback: PropTypes.bool,

    min: PropTypes.instanceOf(Date),
    max: PropTypes.instanceOf(Date),
    earliest: PropTypes.instanceOf(Date),
    latest: PropTypes.instanceOf(Date),

    minRange: function(props, propName, componentName){
        if(props[propName] && !/^P(?!$)(((\d+)Y)?((\d+)M)?((\d+)D)?(T((\d+)H)?((\d+)M)?((\d+)S)?)?)$/.test(props[propName])){
            return new Error(`"${propName}" in "${componentName}" is no valid ISO 8601 Duration`);
        }
    },
    maxRange: function(props, propName, componentName){
        if(props[propName] && !/^P(?!$)(((\d+)Y)?((\d+)M)?((\d+)D)?(T((\d+)H)?((\d+)M)?((\d+)S)?)?)$/.test(props[propName])){
            return new Error(`"${propName}" in "${componentName}" is no valid ISO 8601 Duration`);
        }
    },

    stepsize: function(props, propName, componentName){
        if(props[propName] && !/^P(?!$)(((\d+)Y)?((\d+)M)?((\d+)D)?(T((\d+)H)?((\d+)M)?((\d+)S)?)?)$/.test(props[propName])){
            return new Error(`"${propName}" in "${componentName}" is no valid ISO 8601 Duration`);
        }
    },
    roundStepsize: PropTypes.bool,

    datePlaceholder: PropTypes.string,
    timePlaceholder: PropTypes.string,

    callback: PropTypes.func,

    language: PropTypes.string.isRequired,
    locale: PropTypes.string.isRequired
};

const mapStateToProps = state => {
    return {
        locale: state.global.localization.locale,
        language: state.global.localization.language
    };
};

export default connect(mapStateToProps, null)(DateTimePicker);



// WEBPACK FOOTER //
// ./src/components/UI/DateTimePicker/DateTimePicker.js